import { useEffect } from 'react';
import { useData } from './DataContext';

/**
 * Generates a mandala SVG pattern with custom color
 * @param {string} color - Hex color for the pattern
 * @returns {string} - Data URL for the SVG pattern
 */
const generateMandalaPattern = (color) => {
    // Remove # and encode for SVG
    const encodedColor = encodeURIComponent(color);
    return `url("data:image/svg+xml,%3Csvg width='200' height='200' viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='${encodedColor}' stroke-opacity='0.1' stroke-width='0.5'%3E%3Ccircle cx='100' cy='100' r='80'/%3E%3Ccircle cx='100' cy='100' r='60'/%3E%3Ccircle cx='100' cy='100' r='40'/%3E%3Ccircle cx='100' cy='100' r='20'/%3E%3Cpath d='M100 20 L100 180 M20 100 L180 100'/%3E%3Cpath d='M100 20 Q140 60 100 100 Q60 140 100 180'/%3E%3Cpath d='M100 20 Q60 60 100 100 Q140 140 100 180'/%3E%3Cpath d='M20 100 Q60 60 100 100 Q140 140 180 100'/%3E%3Cpath d='M20 100 Q60 140 100 100 Q140 60 180 100'/%3E%3C/g%3E%3C/svg%3E")`;
};

const generateHeroPattern = (color) => {
    const encodedColor = encodeURIComponent(color);
    return `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='${encodedColor}' stroke-opacity='0.15' stroke-width='0.5'%3E%3Ccircle cx='50' cy='50' r='40'/%3E%3Ccircle cx='50' cy='50' r='20'/%3E%3C/g%3E%3C/svg%3E")`;
};

/**
 * ThemeProvider component that applies theme colors from JSON to CSS variables
 * This makes the UI colors configurable via websiteData.json
 */
const ThemeProvider = ({ children }) => {
    const { data, loading } = useData();

    useEffect(() => {
        if (loading || !data?.ui?.theme) return;

        const theme = data.ui.theme;
        const root = document.documentElement;

        // Map JSON theme keys to CSS variable names
        const themeMapping = {
            colorCream: '--color-cream',
            colorCreamLight: '--color-cream-light',
            colorSaffron: '--color-saffron',
            colorSaffronLight: '--color-saffron-light',
            colorDarkOrange: '--color-dark-orange',
            colorMaroon: '--color-maroon',
            colorGold: '--color-gold',
            colorBrown: '--color-brown',
            colorBrownLight: '--color-brown-light',
            colorTerracotta: '--color-terracotta',
            colorDeepBrown: '--color-deep-brown',
            colorCardBg: '--color-card-bg',
            colorSuccessBg: '--color-success-bg',
            colorSuccessText: '--color-success-text',
            colorErrorBg: '--color-error-bg',
            colorErrorText: '--color-error-text',
        };

        // Apply theme colors to CSS variables
        Object.entries(themeMapping).forEach(([jsonKey, cssVar]) => {
            if (theme[jsonKey]) {
                root.style.setProperty(cssVar, theme[jsonKey]);
            }
        });

        // Also update derived UI colors that depend on base colors
        if (theme.colorCream) {
            root.style.setProperty('--color-bg', theme.colorCream);
        }
        if (theme.colorCreamLight) {
            root.style.setProperty('--color-bg-alt', theme.colorCreamLight);
        }
        if (theme.colorSaffron) {
            root.style.setProperty('--color-border', theme.colorSaffron);
            root.style.setProperty('--color-accent', theme.colorSaffron);
            root.style.setProperty('--color-border-light', `${theme.colorSaffron}66`); // 40% opacity

            // Update patterns with theme color
            root.style.setProperty('--mandala-pattern', generateMandalaPattern(theme.colorSaffron));
            root.style.setProperty('--hero-pattern', generateHeroPattern(theme.colorSaffron));
        }
        if (theme.colorDarkOrange) {
            root.style.setProperty('--color-accent-dark', theme.colorDarkOrange);
        }
        if (theme.colorBrown) {
            root.style.setProperty('--color-text', theme.colorBrown);
        }
        if (theme.colorBrownLight) {
            root.style.setProperty('--color-text-light', theme.colorBrownLight);
        }
        if (theme.colorMaroon) {
            root.style.setProperty('--color-heading', theme.colorMaroon);
        }

    }, [data, loading]);

    return children;
};

export default ThemeProvider;

